import withTranslate from "@pathwright/ux/src/components/lng/withTranslate"
import { OrderType } from "@pathwright/graphql-client/src/generated"
import ConditionalOrderDiscountForm, {
  useCouponExists
} from "@pathwright/features/src/modules/order/ConditionalOrderDiscountForm"
import OrderStripeCheckoutForm from "@pathwright/features/src/modules/order/OrderStripeCheckoutForm"
import { ConnectStores } from "lib/core/store"
import "./Register.css"

const Register = function (props) {
  const {
    price,
    offeringId,
    resourceId,
    discountCode,
    invitationCode,
    useSubscriptionPlanId,
    useGroupSubscriptionId,
    offeringHasGroupProducts,
    submitLabel,
    applyDiscountCode,
    clearDiscountCode,
    attemptToConvert = false
  } = props

  // Only show discount form if there is a price or a discount code has been applied.
  // The applied discount code may have reduced the price to 0, hence the check for !!discountCode.
  const shouldShowDiscountForm = price > 0 || !!discountCode

  const couponExistsInput = shouldShowDiscountForm
    ? {
        resourceId,
        minSeats: 0
      }
    : null

  const couponExists = useCouponExists(couponExistsInput)

  const orderData = {
    offering_id: offeringId,
    coupon_code: discountCode,
    invitation_code: invitationCode,
    use_subscription_plan_id: useSubscriptionPlanId,
    // Cover attemptToConvert submits this form directly. Without the group
    // subscription id, createRegistrationOrder treats a dual-option cohort as
    // one-off and redirects to Stripe even when paymentType=subscription.
    use_group_subscription_id: useGroupSubscriptionId
  }

  return (
    <div className="Register">
      {shouldShowDiscountForm ? (
        <ConditionalOrderDiscountForm
          {...couponExistsInput}
          applyDiscountCode={applyDiscountCode}
          clearDiscountCode={clearDiscountCode}
        />
      ) : null}
      <OrderStripeCheckoutForm
        submitLabel={submitLabel}
        maintenanceModeOverride={price > 0 ? false : true}
        orderType={OrderType.Registration}
        orderData={orderData}
        // Don't block auto-convert on a pending coupon probe — only skip it
        // when we know a coupon exists. Checkout stays clickable while the
        // query is in flight (slow GraphQL used to spin forever here).
        // For group-included $0 registration, wait until the group subscription
        // id is on the order — otherwise Stripe Checkout is created as one-off.
        attemptToConvert={
          attemptToConvert &&
          couponExists === false &&
          (price > 0 ||
            !!useSubscriptionPlanId ||
            !!useGroupSubscriptionId ||
            !offeringHasGroupProducts)
        }
      />
    </div>
  )
}

Register.displayName = "Register"

const mapStateToProps = function (state, ownProps) {
  const { t, offerConversionActionLabel } = ownProps

  const {
    invitation,
    offering,
    discount,
    display_price,
    price,
    includedInGroupSubscription
  } = state.registerStore.state
  const { loadDiscount, clearDiscount, clearRegisterErrors } =
    state.registerStore.action
  const { includedInUserSubscription } = state.resourceStore.request
  const { subscription } = state.subscribeStore.state

  function applyDiscountCode(code) {
    clearRegisterErrors()
    return loadDiscount(code).promise
  }

  function clearDiscountCode() {
    clearDiscount()
  }

  const submitLabel =
    price > 0
      ? t("registration.register.pay_price", { display_price })
      : offerConversionActionLabel || t("registration.register.register")

  const offeringId = offering && offering.id
  const resourceId = offering?.resource_id
  const discountCode = (discount && discount.discount_code) || undefined
  const invitationCode = (invitation && invitation.invite_token) || undefined
  const useSubscriptionPlanId =
    includedInUserSubscription(offering).length > 0
      ? subscription.school_plan.id
      : undefined
  const useGroupSubscriptionId = includedInGroupSubscription?.[0] || null
  const offeringHasGroupProducts = !!(
    offering?.include_with_products && offering.include_with_products.length
  )

  return _.assign(
    {},
    {
      price,
      offeringId,
      resourceId,
      discountCode,
      invitationCode,
      useSubscriptionPlanId,
      useGroupSubscriptionId,
      offeringHasGroupProducts,
      submitLabel,
      applyDiscountCode,
      clearDiscountCode
    },
    ownProps
  )
}

const stores = [
  {
    store: "register",
    watch: [
      "offering",
      "discount",
      "display_price",
      "price",
      "is_registering",
      "includedInGroupSubscription"
    ]
  },
  { store: "resource", watch: [] },
  { store: "subscribe", watch: [] }
]

export default withTranslate(
  ConnectStores(Register, stores, {}, mapStateToProps)
)
